import React from "react";
import {
  Field,
  SectionHeading,
  TextArea,
  TextInput,
} from "./BillingFormControls";

export const NOTE_MAX_LENGTH = 500;
export const REQUESTED_BY_MAX_LENGTH = 200;

export type AuditNoteValues = {
  note: string;
  requestedBy: string;
};

export type AuditNoteErrors = {
  note: string;
  requestedBy: string;
};

export const EMPTY_AUDIT_NOTE: AuditNoteValues = { note: "", requestedBy: "" };
export const EMPTY_AUDIT_NOTE_ERRORS: AuditNoteErrors = {
  note: "",
  requestedBy: "",
};

/**
 * Both fields are mandatory on the billing service too, which answers with a 400
 * when either is missing. Validating here keeps the admin from losing a filled-in
 * form to a round trip.
 */
export function validateAuditNote(values: AuditNoteValues): AuditNoteErrors {
  const note = values.note.trim();
  const requestedBy = values.requestedBy.trim();

  return {
    note: !note
      ? "Note is required"
      : note.length > NOTE_MAX_LENGTH
        ? `Note must be at most ${NOTE_MAX_LENGTH} characters`
        : "",
    requestedBy: !requestedBy
      ? "Requested by is required"
      : requestedBy.length > REQUESTED_BY_MAX_LENGTH
        ? `Requested by must be at most ${REQUESTED_BY_MAX_LENGTH} characters`
        : "",
  };
}

export function hasAuditNoteError(errors: AuditNoteErrors) {
  return Boolean(errors.note || errors.requestedBy);
}

export function trimAuditNote(values: AuditNoteValues): AuditNoteValues {
  return {
    note: values.note.trim(),
    requestedBy: values.requestedBy.trim(),
  };
}

/**
 * The two audit sections on every credit-granting modal: why the change is being
 * made, and who asked for it. One component so the custom plan and top-up modals
 * cannot drift apart.
 */
export default function AuditNoteFields({
  values,
  errors,
  onChange,
  notePlaceholder = "e.g. contract signed for 40k credits at ₹126666 / month",
  requestedByPlaceholder = "e.g. Ravi (Sales)",
}: {
  values: AuditNoteValues;
  errors: AuditNoteErrors;
  onChange: (values: AuditNoteValues) => void;
  notePlaceholder?: string;
  requestedByPlaceholder?: string;
}) {
  const noteLength = values.note.trim().length;

  return (
    <section>
      <SectionHeading title="Audit trail" hint="Required" />
      <div className="rounded-2xl border border-[#eae7ec] bg-[rgba(253,252,253,0.72)] p-4">
        <div className="flex flex-col gap-4">
          <Field
            label="Note"
            required
            hint={`${noteLength}/${NOTE_MAX_LENGTH}`}
            error={errors.note}
            help="Plain text. Shown in the credits table against this change."
          >
            <TextArea
              value={values.note}
              onChange={(note) => onChange({ ...values, note })}
              placeholder={notePlaceholder}
              error={errors.note}
              rows={3}
              maxLength={NOTE_MAX_LENGTH}
            />
          </Field>

          <Field
            label="Requested by"
            required
            error={errors.requestedBy}
          >
            <TextInput
              value={values.requestedBy}
              onChange={(requestedBy) => onChange({ ...values, requestedBy })}
              placeholder={requestedByPlaceholder}
              error={errors.requestedBy}
              maxLength={REQUESTED_BY_MAX_LENGTH}
            />
          </Field>
        </div>
      </div>
    </section>
  );
}
